import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { VaultItem, VaultItemVisibility } from '../types';
import { apiWorker } from '../api/worker';
import toast from 'react-hot-toast';

export const useVaultItems = (nodeId: string) => {
  return useQuery<VaultItem[]>({
    queryKey: ['vault', nodeId],
    queryFn: () => apiWorker.get(`/api/vault/${nodeId}/documents`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useUploadToVault = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { file: File, title: string, description?: string, visibility: VaultItemVisibility }) => {
      const formData = new FormData();
      formData.append('file', data.file);
      
      return apiWorker.post(`/api/vault/${nodeId}/upload`, formData, {
        params: {
          title: data.title,
          description: data.description,
          visibility: data.visibility
        },
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', nodeId] });
      toast.success('File added to vault');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Upload failed');
    }
  });
};

export const useVerifyVaultItem = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: string | { itemId: string; status?: string }) => {
      const itemId = typeof param === 'string' ? param : param.itemId;
      const status = typeof param === 'string' ? 'VERIFIED' : (param.status || 'VERIFIED');
      return apiWorker.patch(`/api/vault/documents/${itemId}/verify`, { status }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', nodeId] });
      toast.success('Document verified');
    }
  });
};

export const useDeleteVaultItem = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (itemId: string) => 
      apiWorker.delete(`/api/vault/items/${itemId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vault', nodeId] });
      toast.success('Item removed from vault');
    }
  });
};
